"use client";

import { c as _c } from "react/compiler-runtime";
import { motion } from "framer-motion";
import { ArrowRight, Ship, MapPin, FileText, Camera, Newspaper, Mic, BookOpen, Anchor } from "lucide-react";
import { ARCHIVE_SECTIONS, SAMPLE_SHIPS } from "@/data/archivesData";
const FONT_STYLE = `
    .archive-section-grain::after {
        content: '';
        position: absolute;
        inset: 0;
        background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.03'/%3E%3C/svg%3E");
        pointer-events: none;
        z-index: 10;
    }
`;
const SECTION_META = {
  documents: {
    icon: FileText,
    accent: "#DF6D2A"
  },
  photographs: {
    icon: Camera,
    accent: "#C9A84C"
  },
  newspapers: {
    icon: Newspaper,
    accent: "#42A5F5"
  },
  "oral-history": {
    icon: Mic,
    accent: "#AB47BC"
  },
  "ships-routes": {
    icon: Ship,
    accent: "#DF6D2A"
  },
  research: {
    icon: BookOpen,
    accent: "#66BB6A"
  }
};
const CORE_SECTIONS = ARCHIVE_SECTIONS.filter(s => !["ships-routes", "research"].includes(s.slug));
const SHIPS_SECTION = ARCHIVE_SECTIONS.find(s => s.slug === "ships-routes");
const RESEARCH_SECTION = ARCHIVE_SECTIONS.find(s => s.slug === "research");
function scrollToExplorer(category) {
  const el = document.getElementById("archive-explorer");
  if (el) {
    el.scrollIntoView({
      behavior: "smooth",
      block: "start"
    });
    window.dispatchEvent(new CustomEvent("archive-filter", {
      detail: {
        category
      }
    }));
  }
}
function ItemTags(t0) {
  const $ = _c(4);
  const {
    items
  } = t0;
  let t1;
  if ($[0] !== items) {
    t1 = items.map(_temp);
    $[0] = items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div className="flex flex-wrap gap-2 mt-5">{t1}</div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp(item) {
  return <span key={item} className="font-body text-[11px] sm:text-[12px] text-white/70 px-3 py-1.5 border border-white/12 bg-white/[0.04] leading-tight">{item}</span>;
}
function SectionCard(t0) {
  const $ = _c(51);
  const {
    section,
    idx
  } = t0;
  let t1;
  if ($[0] !== section.slug) {
    t1 = SECTION_META[section.slug] || {
      icon: FileText,
      accent: "#DF6D2A"
    };
    $[0] = section.slug;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const meta = t1;
  const Icon = meta.icon;
  const t2 = `archive-${section.slug}`;
  let t3;
  let t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      opacity: 0,
      y: 16
    };
    t4 = {
      opacity: 1,
      y: 0
    };
    t5 = {
      once: true
    };
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
  } else {
    t3 = $[2];
    t4 = $[3];
    t5 = $[4];
  }
  const t6 = idx * 0.07;
  let t7;
  if ($[5] !== t6) {
    t7 = {
      duration: 0.45,
      delay: t6
    };
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== meta.accent) {
    t8 = <div className="absolute top-0 left-0 right-0 h-[3px] opacity-80 group-hover:opacity-100 transition-opacity" style={{
      background: meta.accent
    }} />;
    $[7] = meta.accent;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== meta.accent) {
    t9 = {
      color: meta.accent
    };
    $[9] = meta.accent;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== Icon) {
    t10 = <Icon size={18} strokeWidth={1.5} />;
    $[11] = Icon;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== t10 || $[14] !== t9) {
    t11 = <div className="w-10 h-10 flex items-center justify-center border border-white/15 shrink-0" style={t9}>{t10}</div>;
    $[13] = t10;
    $[14] = t9;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== section.id) {
    t12 = <span className="font-body text-[10px] tracking-[0.22em] uppercase text-white/40">{section.id}</span>;
    $[16] = section.id;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== t11 || $[19] !== t12) {
    t13 = <div className="flex items-center gap-3">{t11}{t12}</div>;
    $[18] = t11;
    $[19] = t12;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== section.category) {
    t14 = () => scrollToExplorer(section.category);
    $[21] = section.category;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <ArrowRight size={11} />;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== t14) {
    t16 = <button type="button" onClick={t14} className="font-body text-[10px] tracking-[0.14em] uppercase text-[#C9A84C] hover:text-white flex items-center gap-1 transition-colors shrink-0 opacity-0 group-hover:opacity-100">Browse{t15}</button>;
    $[24] = t14;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== t13 || $[27] !== t16) {
    t17 = <div className="flex items-start justify-between gap-4 mb-5">{t13}{t16}</div>;
    $[26] = t13;
    $[27] = t16;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== section.title) {
    t18 = <h3 className="font-display text-[21px] sm:text-[24px] text-[#F4EFE6] mb-2.5 leading-tight group-hover:text-[#C9A84C] transition-colors">{section.title}</h3>;
    $[29] = section.title;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== section.description) {
    t19 = <p className="font-body text-[13px] text-white/50 leading-[1.65]">{section.description}</p>;
    $[31] = section.description;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== section.items) {
    t20 = section.items && <ItemTags items={section.items} />;
    $[33] = section.items;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  let t21;
  if ($[35] !== section.category) {
    t21 = () => scrollToExplorer(section.category);
    $[35] = section.category;
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  let t22;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <ArrowRight size={12} />;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  let t23;
  if ($[38] !== t21) {
    t23 = <button type="button" onClick={t21} className="mt-5 pt-4 border-t border-white/8 font-body text-[10px] tracking-[0.16em] uppercase text-white/45 hover:text-[#DF6D2A] flex items-center justify-between transition-colors lg:hidden">Explore collection{t22}</button>;
    $[38] = t21;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  let t24;
  if ($[40] !== t17 || $[41] !== t18 || $[42] !== t19 || $[43] !== t20 || $[44] !== t23) {
    t24 = <div className="p-6 sm:p-7 flex flex-col flex-1">{t17}{t18}{t19}{t20}{t23}</div>;
    $[40] = t17;
    $[41] = t18;
    $[42] = t19;
    $[43] = t20;
    $[44] = t23;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  let t25;
  if ($[46] !== t2 || $[47] !== t24 || $[48] !== t7 || $[49] !== t8) {
    t25 = <motion.article id={t2} initial={t3} whileInView={t4} viewport={t5} transition={t7} className="group relative border border-white/10 bg-gradient-to-br from-white/[0.06] to-transparent hover:border-white/20 transition-all duration-300 flex flex-col">{t8}{t24}</motion.article>;
    $[46] = t2;
    $[47] = t24;
    $[48] = t7;
    $[49] = t8;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  return t25;
}
const ArchiveSections = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    const shipsSubsections = SHIPS_SECTION?.subsections || [];
    const databaseFields = shipsSubsections.find(_temp2)?.fields || [];
    const routes = shipsSubsections.find(_temp3)?.routes || [];
    t0 = <><style>{FONT_STYLE}</style><section className="bg-[#0F1C2E] py-16 sm:py-24 md:py-28 relative overflow-hidden archive-section-grain"><div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 relative z-20"><div className="max-w-[760px] mb-12 sm:mb-14"><div className="flex items-center gap-2 sm:gap-3 mb-4"><div className="w-8 h-px bg-[#DF6D2A]" /><span className="font-body text-[10.5px] tracking-[0.25em] uppercase text-[#DF6D2A]">Digital Archive</span></div><h2 className="font-display text-[32px] sm:text-[40px] md:text-[48px] text-[#F4EFE6] leading-[1.05] mb-4">Preserving Original <em className="text-[#C9A84C] italic">Records</em></h2><p className="font-body text-[14px] sm:text-[16px] text-white/55 font-light leading-[1.75]">Original indenture contracts, government records, plantation registers, migration documents, photographs, oral histories, and scholarly publications.</p></div><div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 mb-4 sm:mb-5">{CORE_SECTIONS.map(_temp4)}</div>{SHIPS_SECTION && <motion.div id={`archive-${SHIPS_SECTION.slug}`} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="border border-white/10 bg-[#1A2C42]/60 mb-4 sm:mb-5 overflow-hidden"><div className="grid grid-cols-1 lg:grid-cols-12"><div className="lg:col-span-4 p-6 sm:p-8 border-b lg:border-b-0 lg:border-r border-white/10 flex flex-col"><div className="flex items-center gap-3 mb-5"><div className="w-10 h-10 flex items-center justify-center border border-[#DF6D2A]/40 text-[#DF6D2A]"><Ship size={18} strokeWidth={1.5} /></div><span className="font-body text-[10px] tracking-[0.22em] uppercase text-white/40">{SHIPS_SECTION.id}</span></div><h3 className="font-display text-[26px] sm:text-[30px] text-[#F4EFE6] mb-3 leading-tight">{SHIPS_SECTION.title}</h3><p className="font-body text-[13px] sm:text-[14px] text-white/50 leading-[1.65] mb-6">{SHIPS_SECTION.description}</p><div className="mt-auto"><p className="font-body text-[10px] tracking-[0.2em] uppercase text-[#C9A84C] mb-3 flex items-center gap-2"><MapPin size={12} />5.2 Indenture Routes</p><div className="flex flex-wrap gap-2">{routes.map(_temp5)}</div></div></div><div className="lg:col-span-8 p-6 sm:p-8"><div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5"><div><p className="font-body text-[10px] tracking-[0.2em] uppercase text-[#DF6D2A] mb-1 flex items-center gap-2"><Anchor size={12} />5.1 Ships Database</p><p className="font-body text-[12px] text-white/45">Searchable records of indenture-era vessels</p></div><button type="button" onClick={_temp6} className="font-body text-[10px] tracking-[0.14em] uppercase text-[#F4EFE6] border border-white/20 px-4 py-2 hover:bg-[#DF6D2A] hover:border-[#DF6D2A] transition-colors shrink-0 self-start sm:self-auto">Browse all ships</button></div><div className="flex flex-wrap gap-2 mb-5 pb-5 border-b border-white/8">{databaseFields.map(_temp7)}</div><div className="space-y-2">{SAMPLE_SHIPS.map(_temp8)}</div></div></div></motion.div>}{RESEARCH_SECTION && <motion.article id={`archive-${RESEARCH_SECTION.slug}`} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="border border-white/10 bg-gradient-to-r from-white/[0.05] to-transparent overflow-hidden"><div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr]"><div className="p-6 sm:p-8 border-b lg:border-b-0 lg:border-r border-white/10"><div className="flex items-center gap-3 mb-5"><div className="w-10 h-10 flex items-center justify-center border border-[#66BB6A]/40 text-[#66BB6A]"><BookOpen size={18} strokeWidth={1.5} /></div><span className="font-body text-[10px] tracking-[0.22em] uppercase text-white/40">{RESEARCH_SECTION.id}</span></div><h3 className="font-display text-[26px] sm:text-[30px] text-[#F4EFE6] mb-3 leading-tight">{RESEARCH_SECTION.title}</h3><p className="font-body text-[13px] sm:text-[14px] text-white/50 leading-[1.65] mb-6">{RESEARCH_SECTION.description}</p><button type="button" onClick={_temp9} className="font-body text-[10px] tracking-[0.14em] uppercase text-[#F4EFE6] border border-white/20 px-5 py-2.5 hover:bg-[#DF6D2A] hover:border-[#DF6D2A] transition-colors inline-flex items-center gap-2">Browse publications<ArrowRight size={12} /></button></div><div className="p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 content-center">{RESEARCH_SECTION.items?.map(_temp10)}</div></div></motion.article>}</div></section></>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
export default ArchiveSections;
function _temp2(s) {
  return s.fields;
}
function _temp3(s_0) {
  return s_0.routes;
}
function _temp4(section, idx) {
  return <SectionCard key={section.id} section={section} idx={idx} />;
}
function _temp5(route) {
  return <span key={route} className="font-body text-[11px] text-white/75 px-3 py-1.5 bg-[#0F1C2E]/60 border border-white/10 flex items-center gap-1.5"><ArrowRight size={10} className="text-[#DF6D2A] shrink-0" />{route}</span>;
}
function _temp6() {
  return scrollToExplorer(SHIPS_SECTION.category);
}
function _temp7(field) {
  return <span key={field} className="font-body text-[10px] tracking-[0.08em] uppercase text-white/50 px-2.5 py-1 border border-dashed border-white/15">{field}</span>;
}
function _temp8(ship, i) {
  return <div key={ship.name} className={`grid grid-cols-[1fr_auto] sm:grid-cols-[140px_60px_1fr_1fr_70px] gap-2 sm:gap-4 items-center px-4 py-3 ${i % 2 === 0 ? "bg-white/[0.03]" : "bg-transparent"} hover:bg-[#DF6D2A]/10 transition-colors`}><span className="font-display text-[15px] sm:text-[16px] text-[#F4EFE6]">{ship.name}</span><span className="font-body text-[12px] text-white/45 sm:text-center hidden sm:block">{ship.year}</span><span className="font-body text-[12px] text-white/55 hidden sm:block truncate">{ship.departure}</span><span className="font-body text-[12px] text-white/55 hidden sm:block truncate">→ {ship.arrival}</span><span className="font-body text-[12px] text-[#C9A84C] text-right sm:text-center">{ship.labourers}</span><span className="font-body text-[11px] text-white/40 sm:hidden col-span-2">{ship.year} · {ship.departure} → {ship.arrival}</span></div>;
}
function _temp9() {
  return scrollToExplorer(RESEARCH_SECTION.category);
}
function _temp10(item, i_0) {
  return <div key={item} className="group/item flex items-start gap-3 p-4 border border-white/10 bg-[#0F1C2E]/30 hover:border-[#DF6D2A]/40 hover:bg-[#0F1C2E]/50 transition-all cursor-default"><span className="font-display text-[22px] text-[#DF6D2A]/50 leading-none group-hover/item:text-[#DF6D2A] transition-colors">{String(i_0 + 1).padStart(2, "0")}</span><p className="font-body text-[13px] sm:text-[14px] text-white/75 leading-snug pt-0.5">{item}</p></div>;
}